import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import Sidebar from '../components/Sidebar';
import ProjectCard, { Project } from '../components/ProjectCard';
import NewProjectModal from '../components/NewProjectModal';
import FastTrackWizardModal from '../components/FastTrackWizardModal';
import DefaultsCard, { type DefaultsCardDefaults } from '../components/DefaultsCard';
import PushPermissionBanner from '../components/PushPermissionBanner';
import { registerServiceWorker } from '../lib/pushSubscribe';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import { getOnboardingPathForTier } from '../lib/tier';

// CFGIA.A4 (#1539) — per-user "don't show again" key for the onboarding banner.
// Must match the server allowlist ACKNOWLEDGEABLE_FEATURES in
// apps/api/src/routes/feature-acknowledgements.ts (the endpoint refuses any other
// key). Persisting the dismiss in users.feature_acknowledgements (not
// sessionStorage) makes it stick per user across sessions and devices.
export const ONBOARDING_BANNER_FEATURE = 'onboarding_setup_banner_dismissed_v1';
// #1825 (AC4) — per-user "don't show again" for the dashboard "Complete
// your plan setup" nudge, paid-plan signups only. Must match the server
// allowlist ACKNOWLEDGEABLE_FEATURES in
// apps/api/src/routes/feature-acknowledgements.ts. Distinct key from
// ONBOARDING_BANNER_FEATURE above — dismissing one never dismisses the other.
export const PLAN_SETUP_BANNER_FEATURE = 'plan_setup_banner_dismissed_v1';
const PAID_PLAN_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  'platform-partner': 'Platform Partner',
};
const DEFAULTS_CARD_DISMISSED_KEY = 'defaults_card_dismissed_session';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  // Sprint 12.5 ACC.C1 (#421) — separate state for the dedicated Fast Track
  // wizard. Independent of the standard "New Project" modal: the two flows
  // are different shapes (single step vs multi-field form) and the issue
  // spec asks for a separate "New Fast Track" button surface.
  const [showFastTrack, setShowFastTrack] = useState(false);
  const { myTenant } = useMyTenant();
  // CFGIA.A4 (#1539) — banner visibility is driven by a LIVE completeness read
  // (setupComplete: null = unknown until the server answers, so the banner stays
  // hidden until then) plus a per-user dismiss (bannerDismissed, hydrated from
  // feature_acknowledgements). This replaces the old onboarding_status terminal
  // flag + per-session sessionStorage dismiss.
  const [setupComplete, setSetupComplete] = useState<boolean | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // #1825 (AC4) — intendedPlan hydrated from the user's own row (durable,
  // survives refresh/re-login — not a ?plan= URL param, which wouldn't
  // survive the email-verification redirect round-trip reliably). null =
  // either not yet loaded or genuinely no plan intent captured at signup.
  const [intendedPlan, setIntendedPlan] = useState<string | null>(null);
  const [planBannerDismissed, setPlanBannerDismissed] = useState(false);
  // D3 (#1247) — the endpoint now also returns the ring-flow completion signal
  // (setupComplete / setupStepsRemaining) so the card copy can be accurate.
  // #1247 access-regression follow-on — and setupAccessible (per-caller /setup
  // eligibility) to gate the "Continue setup →" CTA.
  const [defaultsCard, setDefaultsCard] = useState<{
    show: boolean;
    defaults?: DefaultsCardDefaults;
    setupComplete?: boolean;
    setupStepsRemaining?: number;
    setupAccessible?: boolean;
  } | null>(null);

  const statusFilter = (searchParams.get('status') ?? 'ACTIVE').toUpperCase() as 'ACTIVE' | 'ARCHIVED' | 'DELETED';

  // #1807 — `?create=1` opens the New Project form (the solo onboarding
  // wizard's exit, and the `/projects/new` redirect). Derived from the URL,
  // not copied into state, so it needs no effect; closing drops the param so
  // a refresh doesn't reopen it.
  const createRequested = searchParams.get('create') === '1';
  const closeNewProject = () => {
    setShowModal(false);
    if (createRequested) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  };

  const isAdmin = isAdminRole(myTenant?.role);

  // CFGIA.A4 (#1539) — show only while config is genuinely incomplete (live
  // heuristic from /tenants/me/setup-completeness, NOT the stored
  // onboarding_status flag) AND the user hasn't dismissed it. setupComplete ===
  // false (not just falsy) so the banner never flashes during the unknown (null)
  // window before the server answers.
  const showOnboardingBanner = isAdmin && setupComplete === false && !bannerDismissed;

  // #1825 (AC4) — paid-plan signups only ('individual' has nothing to
  // "complete" — it's the free tier). Admin-gated for the same reason as
  // showOnboardingBanner: an invited non-admin member didn't choose the
  // plan and has no entitlement to act on this nudge.
  const showPlanSetupBanner =
    isAdmin && !!intendedPlan && intendedPlan in PAID_PLAN_LABELS && !planBannerDismissed;

  const handleDismissBanner = () => {
    // Optimistic hide + persist per-user (fail-OPEN: a failed POST just means the
    // banner may reappear next session, never a stuck UI). Mirrors DISC.C1's
    // ClaudeDesignTour acknowledge-feature flow.
    setBannerDismissed(true);
    apiFetch('/users/me/acknowledge-feature', {
      method: 'POST',
      body: JSON.stringify({ feature: ONBOARDING_BANNER_FEATURE }),
    }).catch(err => {
      console.error('[ProjectsPage] Failed to persist onboarding banner dismiss:', err);
    });
  };

  // #1825 (AC4) — same optimistic-hide + fail-OPEN persist as handleDismissBanner.
  const handleDismissPlanBanner = () => {
    setPlanBannerDismissed(true);
    apiFetch('/users/me/acknowledge-feature', {
      method: 'POST',
      body: JSON.stringify({ feature: PLAN_SETUP_BANNER_FEATURE }),
    }).catch(err => {
      console.error('[ProjectsPage] Failed to persist plan-setup banner dismiss:', err);
    });
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch<Project[]>(`/projects?status=${statusFilter}`);
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Pre-register the push service worker on mount, mirroring DashboardPage.
  // enablePushNotifications() awaits serviceWorker.ready inside the click
  // handler with a 5s timeout — without a pre-warm, the first "Enable" click
  // on a cold /projects load races SW install + activation and can fail
  // closed. Idempotent: the browser dedupes by script URL.
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  useEffect(() => {
    // Short-circuit — skip the round-trip if this session already dismissed it
    if (sessionStorage.getItem(DEFAULTS_CARD_DISMISSED_KEY) === '1') {
      return;
    }
    apiFetch<{ show: boolean; defaults?: DefaultsCardDefaults; setupComplete?: boolean; setupStepsRemaining?: number; setupAccessible?: boolean }>('/tenants/me/defaults-card')
      .then(setDefaultsCard)
      .catch(() => {});
  }, []);

  // CFGIA.A4 (#1539) — hydrate the onboarding banner: a live completeness read
  // gates it, the per-user acknowledgement silences it. Admins only — the wizard
  // 403s non-admins and the endpoint already returns complete:true for them.
  // Both fetches fail-OPEN: a completeness error leaves setupComplete null (banner
  // stays hidden, never nags on a transient failure); an ack-fetch error leaves
  // bannerDismissed false (treat as not-acknowledged, mirroring ClaudeDesignTour).
  useEffect(() => {
    if (!isAdmin) return;
    apiFetch<{ complete: boolean; missing: string[] }>('/tenants/me/setup-completeness')
      .then(r => setSetupComplete(r.complete))
      .catch(() => {});
    apiFetch<{ feature_acknowledgements: Record<string, boolean> }>('/users/me/feature-acknowledgements')
      .then(r => {
        setBannerDismissed(r.feature_acknowledgements?.[ONBOARDING_BANNER_FEATURE] === true);
        // #1825 (AC4) — same response, second key. Avoids a third
        // round-trip just to check one more flag in the same map.
        setPlanBannerDismissed(r.feature_acknowledgements?.[PLAN_SETUP_BANNER_FEATURE] === true);
      })
      .catch(() => {});
    // #1825 (AC4) — fail-OPEN like the two calls above: an error leaves
    // intendedPlan null, so showPlanSetupBanner stays false rather than
    // ever nagging on a transient fetch failure.
    apiFetch<{ intended_plan: string | null }>('/users/me/intended-plan')
      .then(r => setIntendedPlan(r.intended_plan))
      .catch(() => {});
  }, [isAdmin]);

  const handleDismissDefaults = () => {
    // Optimistic UI + session-level short-circuit
    setDefaultsCard({ show: false });
    sessionStorage.setItem(DEFAULTS_CARD_DISMISSED_KEY, '1');
    apiFetch('/tenants/me/defaults-card/dismiss', { method: 'POST' }).catch(err => {
      console.error('[ProjectsPage] Failed to dismiss defaults card:', err);
    });
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />

      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between px-8 py-5" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Projects</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* ACC.C1 (#421) — separate "New Fast Track" button. Distinct
                visual treatment (amber outline, matches the FT badge on
                project cards) so users can tell at a glance the two
                creation flows are different. */}
            <button
              onClick={() => setShowFastTrack(true)}
              data-testid="new-fast-track-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 16px',
                background: 'transparent',
                color: 'var(--accent-amber, #d97706)',
                fontFamily: 'var(--font-display)',
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--accent-amber, #d97706)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>⚡</span>
              New Fast Track
            </button>
            <button
              onClick={() => setShowModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 20px',
                background: 'var(--primary)',
                color: 'white',
                fontFamily: 'var(--font-display)',
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                cursor: 'pointer',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-primary)')}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
              New Project
            </button>
          </div>
        </div>

        {/* Onboarding banner */}
        {showOnboardingBanner && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 24px',
              background: 'linear-gradient(90deg, var(--primary) 0%, #6366f1 100%)',
              color: '#fff',
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚙</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, margin: 0 }}>
                Complete your organisation setup to unlock all BluePrint features.
              </p>
            </div>
            <button
              onClick={() => navigate(getOnboardingPathForTier(myTenant?.subscriptionTier))}
              style={{
                flexShrink: 0,
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                fontWeight: 600,
                padding: '7px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 'var(--radius-sm)',
                color: '#fff',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
            >
              Complete Setup →
            </button>
            <button
              onClick={handleDismissBanner}
              aria-label="Dismiss"
              style={{
                flexShrink: 0,
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: 16,
                padding: 4,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* #1825 (AC4) — plan-setup nudge, paid-plan signups only. Separate
            from the onboarding banner above — a tenant can be fully
            onboarded (setupComplete: true) and still have an un-actioned
            paid plan intent, or vice versa; independent dismiss state
            (PLAN_SETUP_BANNER_FEATURE) so acting on one never silences
            the other. */}
        {showPlanSetupBanner && intendedPlan && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 24px',
              background: 'var(--surface-alt)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>✦</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                Complete your {PAID_PLAN_LABELS[intendedPlan] ?? intendedPlan} plan setup
              </p>
            </div>
            <button
              onClick={handleDismissPlanBanner}
              aria-label="Dismiss"
              style={{
                flexShrink: 0,
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: 16,
                padding: 4,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', padding: '0 var(--space-8)' }}>
          {(['ACTIVE', 'ARCHIVED', 'DELETED'] as const).map(tab => {
            const isActive = statusFilter === tab;
            const label = tab === 'ACTIVE' ? 'Active' : tab === 'ARCHIVED' ? 'Archived' : 'Deleted';
            return (
              <button
                key={tab}
                onClick={() => setSearchParams(tab === 'ACTIVE' ? {} : { status: tab.toLowerCase() }, { replace: true })}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  transition: 'color 150ms, border-color 150ms',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="px-8 py-6">
          {/*
            Web Push permission banner. Duplicated here from DashboardPage
            because PLATFORM_PARTNER users are redirected from /dashboard to
            /projects on login (see LoginPage.handleSubmit → tier router) —
            the banner used to "flash" on the dashboard and then disappear
            on redirect before the user could interact with it. The banner
            component itself handles all gating (push supported, permission
            === 'default', localStorage dismissal) so rendering it in both
            places is idempotent.
          */}
          <PushPermissionBanner />

          {/* Defaults card after SME onboarding */}
          {defaultsCard?.show && defaultsCard.defaults && (
            <DefaultsCard
              defaults={defaultsCard.defaults}
              onDismiss={handleDismissDefaults}
              onCreateProject={() => setShowModal(true)}
              setupComplete={defaultsCard.setupComplete}
              setupStepsRemaining={defaultsCard.setupStepsRemaining}
              setupAccessible={defaultsCard.setupAccessible}
            />
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-lg p-5"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderTop: '3px solid var(--border)' }}
                >
                  <div className="flex justify-between mb-3">
                    <div className="h-5 bg-gray-200 rounded w-2/3" />
                    <div className="h-5 bg-gray-100 rounded w-1/5" />
                  </div>
                  <div className="h-4 bg-gray-100 rounded w-1/2 mb-3" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && projects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 text-2xl" style={{ background: 'var(--surface-alt)' }}>
                📋
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {statusFilter === 'ACTIVE' ? 'No projects yet' : `No ${statusFilter.toLowerCase()} projects`}
              </h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
                {statusFilter === 'ACTIVE' ? 'Create your first project to get started' : `You have no ${statusFilter.toLowerCase()} projects.`}
              </p>
              {statusFilter === 'ACTIVE' && <button
                onClick={() => setShowModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 20px',
                  background: 'var(--primary)',
                  color: 'white',
                  fontFamily: 'var(--font-display)',
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-primary)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
                New Project
              </button>}
            </div>
          )}

          {/* Project grid */}
          {!loading && !error && projects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {projects.map(project => (
                <ProjectCard key={project.id} project={project} onRestore={fetchProjects} />
              ))}
            </div>
          )}
        </div>
      </main>

      {showFastTrack && (
        <FastTrackWizardModal onClose={() => setShowFastTrack(false)} onSuccess={fetchProjects} />
      )}
      {(showModal || createRequested) && (
        <NewProjectModal onClose={closeNewProject} onSuccess={fetchProjects} />
      )}
    </div>
  );
}
